import React, { useMemo, useState } from 'react'
import moment from 'moment-timezone'
import {
  ArrowsDownUpIcon,
  PencilSimpleIcon,
  BriefcaseIcon,
} from '@phosphor-icons/react'
import { Engagement } from '../../types/Engagement'

type SortColumn =
  | 'projectName'
  | 'client.name'
  | 'team.name'
  | 'startDate'
  | 'endDate'

interface Props {
  engagements: Engagement[]
  onRowClick: (engagement: Engagement) => void
  onEdit: (engagement: Engagement) => void
}

export const getEngagementStatus = (
  engagement: Engagement,
): 'Active' | 'Inactive' => {
  if (
    engagement.endDate &&
    moment(engagement.endDate).isBefore(moment(), 'day')
  ) {
    return 'Inactive'
  }
  return 'Active'
}

const EngagementsTable = ({ engagements, onRowClick, onEdit }: Props) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>('projectName')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const sortedEngagements = useMemo(() => {
    const sorted = [...engagements].sort((a, b) => {
      let comparison = 0
      if (sortColumn === 'projectName') {
        comparison = a.projectName.localeCompare(b.projectName)
      } else if (sortColumn === 'client.name') {
        comparison = (a.client?.name || '').localeCompare(b.client?.name || '')
      } else if (sortColumn === 'team.name') {
        comparison = (a.team?.name || '').localeCompare(b.team?.name || '')
      } else if (sortColumn === 'startDate') {
        comparison = moment(a.startDate).unix() - moment(b.startDate).unix()
      } else if (sortColumn === 'endDate') {
        comparison = moment(a.endDate).unix() - moment(b.endDate).unix()
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })
    return sorted
  }, [engagements, sortColumn, sortOrder])

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  if (engagements.length === 0) {
    return (
      <div className="management-engagement__empty">
        <div className="management-engagement__empty-icon">
          <BriefcaseIcon size={48} weight="regular" color="#717182" />
        </div>
        <p className="management-engagement__empty-label">
          No engagements found. Try adjusting your search or filters.
        </p>
      </div>
    )
  }

  return (
    <div className="management-engagement__table-card">
      <table className="management-engagement__table">
        <thead className="management-engagement__table-head">
          <tr>
            <th
              className="management-engagement__table-th"
              style={{ width: '22%' }}
            >
              <button
                type="button"
                className="management-engagement__sort-btn"
                onClick={() => handleSort('projectName')}
              >
                Team Code Name
                <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
              </button>
            </th>
            <th
              className="management-engagement__table-th"
              style={{ width: '22%' }}
            >
              <button
                type="button"
                className="management-engagement__sort-btn"
                onClick={() => handleSort('client.name')}
              >
                Client
                <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
              </button>
            </th>
            <th
              className="management-engagement__table-th"
              style={{ width: '14%' }}
            >
              <button
                type="button"
                className="management-engagement__sort-btn"
                onClick={() => handleSort('team.name')}
              >
                Team
                <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
              </button>
            </th>
            <th
              className="management-engagement__table-th"
              style={{ width: '16%' }}
            >
              <button
                type="button"
                className="management-engagement__sort-btn"
                onClick={() => handleSort('startDate')}
              >
                Start Date
                <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
              </button>
            </th>
            <th
              className="management-engagement__table-th"
              style={{ width: '16%' }}
            >
              <button
                type="button"
                className="management-engagement__sort-btn"
                onClick={() => handleSort('endDate')}
              >
                End Date
                <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
              </button>
            </th>
            <th className="management-engagement__table-th management-engagement__table-th--status">
              Status
            </th>
            <th className="management-engagement__table-th management-engagement__table-th--actions" />
          </tr>
        </thead>
        <tbody>
          {sortedEngagements.map(engagement => {
            const status = getEngagementStatus(engagement)
            return (
              <tr
                key={engagement.id}
                className="management-engagement__table-row"
                onClick={() => onRowClick(engagement)}
              >
                <td className="management-engagement__table-td">
                  {engagement.projectName}
                </td>
                <td className="management-engagement__table-td">
                  {engagement.client?.name || ''}
                </td>
                <td className="management-engagement__table-td">
                  {engagement.team?.name || ''}
                </td>
                <td className="management-engagement__table-td">
                  {moment(engagement.startDate).format('MM/DD/YYYY')}
                </td>
                <td className="management-engagement__table-td">
                  {engagement.endDate
                    ? moment(engagement.endDate).format('MM/DD/YYYY')
                    : ''}
                </td>
                <td className="management-engagement__table-td management-engagement__table-td--status">
                  <span
                    className={`management-engagement__badge management-engagement__badge--${status.toLowerCase()}`}
                  >
                    {status}
                  </span>
                </td>
                <td className="management-engagement__table-td management-engagement__table-td--actions">
                  <button
                    type="button"
                    className="management-engagement__edit-btn"
                    aria-label={`Edit ${engagement.projectName}`}
                    onClick={e => {
                      e.stopPropagation()
                      onEdit(engagement)
                    }}
                  >
                    <PencilSimpleIcon size={18} weight="regular" />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default EngagementsTable
